import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { get } from 'lodash-es';

import {
  AdComponentDefaultProps,
  AdComponentPropTypes,
} from 'site-modules/shared/components/ad-unit/ad-unit-prop-types';
import { IncentivesSiteServedAd } from 'client/site-modules/shared/components/native-ad/incentives-site-served-ad/incentives-site-served-ad';
import { IncentivesSiteServedAdFeaturesSpecs } from 'site-modules/shared/components/native-ad/incentives-site-served-ad/incentives-site-served-ad-features-specs';
import { IncentivesSiteServedAdCtaOnly } from 'client/site-modules/shared/components/native-ad/incentives-site-served-ad/incentives-site-served-ad-cta-only';

import { NativeAd } from 'site-modules/shared/components/ad-unit/native-ad';
import { INCENTIVES_AD } from 'client/site-modules/shared/components/native-ad/utils/constants';
import { withSiteServedAdWrapper } from 'client/site-modules/shared/components/native-ad/site-served-ad-wrapper/site-served-ad-wrapper';
import {
  IncentivesCreativeConfigDefaultProps,
  IncentivesCreativeConfigPropTypes,
} from 'client/data/models/native-ads-creative-config';

const DATA_TEST = 'incentives-ad';

export function IncentivesAdUI({
  wrapperClass = AdComponentDefaultProps.wrapperClass,
  cardWrapper = null,
  mobile = AdComponentDefaultProps.mobile,
  slotRenderEndListener = AdComponentDefaultProps.slotRenderEndListener,
  includeMrectAdCall = false,
  withDFPFallback,
  withSiteServed,
  siteServedAdsEnabled = AdComponentDefaultProps.siteServedAdsEnabled,
  position = AdComponentDefaultProps.position,
  creativeConfig = IncentivesCreativeConfigDefaultProps,
  currentVehicle = AdComponentDefaultProps.currentVehicle,
  vehicle = AdComponentDefaultProps.vehicle,
  placeholderComponent = AdComponentDefaultProps.placeholderComponent,
  placeholderComponentProps = AdComponentDefaultProps.placeholderComponentProps,
  customTargeting = AdComponentDefaultProps.customTargeting,
  excludeFilmstrip = false,
  horizontalLayout = false,
  plainLayout = false,
  dropShadowLayout = false,
  showDropShadow = false,
  fallbackNativeStyle = 'vdp,filmstripincentive',
  ivtClassName = AdComponentDefaultProps.ivtClassName,
  isFeaturesSpecsLayout = false,
  ads11455Ads11617Recipe = 'ctrl',
  ads12450Recipe = 'ctrl',
  onIncentivesAdLoadedCallback = null,
}) {
  const SiteServedIncentive = (
    <div data-test={DATA_TEST} className={classnames(wrapperClass, ivtClassName, { 'd-none': !withSiteServed })}>
      {withSiteServed &&
        (isFeaturesSpecsLayout ? (
          <IncentivesSiteServedAdFeaturesSpecs
            cardWrapper={cardWrapper}
            mobile={mobile}
            position={position}
            creativeConfig={creativeConfig}
            vehicle={currentVehicle || vehicle}
            customTargeting={customTargeting}
            noBorder
          />
        ) : (
          <IncentivesSiteServedAd
            cardWrapper={cardWrapper}
            mobile={mobile}
            position={position}
            creativeConfig={creativeConfig}
            vehicle={currentVehicle || vehicle}
            customTargeting={customTargeting}
            horizontalLayout={horizontalLayout}
            plainLayout={plainLayout}
            dropShadowLayout={dropShadowLayout}
            showDropShadow={showDropShadow}
            ads11455Ads11617Recipe={ads11455Ads11617Recipe}
          />
        ))}
    </div>
  );

  const nativeAdProps = {
    customTargeting,
    refreshDisableWhenZipChanged: siteServedAdsEnabled,
    placeholderComponent,
    placeholderComponentProps,
    position,
    slotRenderEndListener,
    siteServedAdsUpdater: get(creativeConfig, 'siteServedAdsUpdater'),
  };

  const adsWithDFPFallback = (
    <NativeAd
      wiredBreakpoints={{
        lg: true,
        xl: true,
      }}
      nativeStyle="vdp"
      {...nativeAdProps}
      useMinHeight
    />
  );

  const adsWithDFPFallbackFilmstrip = (
    <NativeAd
      wiredBreakpoints={{
        lg: true,
        xl: true,
      }}
      nativeStyle={fallbackNativeStyle}
      {...nativeAdProps}
      useMinHeight
    />
  );

  const adsWithDFPFallbackWrapper = excludeFilmstrip ? adsWithDFPFallback : adsWithDFPFallbackFilmstrip;
  const renderDfpFallback = (
    <div
      className={classnames('incentive-ad w-100', ivtClassName, { [wrapperClass]: !placeholderComponent })}
      data-test={DATA_TEST}
    >
      {includeMrectAdCall ? (
        <NativeAd
          adNameWired="mrect,native"
          adNameMobile="mmrect,mnative"
          wiredBreakpoints={{
            lg: true,
            xl: true,
          }}
          nativeStyle="vdp,mediumrectangle"
          customSizes={[[1, 1], [300, 250], 'fluid']}
          {...nativeAdProps}
        />
      ) : (
        adsWithDFPFallbackWrapper
      )}
    </div>
  );

  if (['chal1', 'chal2'].includes(ads12450Recipe)) {
    return (
      <IncentivesSiteServedAdCtaOnly
        onAdLoadedCallback={onIncentivesAdLoadedCallback}
        cardWrapper={cardWrapper}
        position={position}
        creativeConfig={creativeConfig}
        vehicle={currentVehicle || vehicle}
        customTargeting={customTargeting}
        ads11455Ads11617Recipe={ads11455Ads11617Recipe}
        mobile={mobile}
      />
    );
  }

  if (isFeaturesSpecsLayout) {
    return (
      <Fragment>
        {/* To make it only show site served ads, showOnlySiteServed props need to be passed and set to true in order to make withDFPFallback=false */}
        {withDFPFallback ? (
          <Fragment>
            <div className="mb-2_5">
              <span className="text-cool-gray-10 size-12 float-end">Ad</span>
            </div>
            {renderDfpFallback}
          </Fragment>
        ) : (
          <Fragment>
            <div className="mb-1">
              <h2 className="heading-4 d-inline-block mb-0">Incentives</h2>
              <span className="text-cool-gray-10 size-12 float-end">Ad</span>
            </div>
            {SiteServedIncentive}
          </Fragment>
        )}
      </Fragment>
    );
  }
  return (
    <Fragment>
      {/* To make it only show site served ads, showOnlySiteServed props need to be passed and set to true in order to make withDFPFallback=false */}
      {withDFPFallback ? renderDfpFallback : SiteServedIncentive}
    </Fragment>
  );
}

IncentivesAdUI.propTypes = {
  ...AdComponentPropTypes,
  creativeConfig: IncentivesCreativeConfigPropTypes,
  cardWrapper: PropTypes.string,
  includeMrectAdCall: PropTypes.bool,
  excludeFilmstrip: PropTypes.bool,
  horizontalLayout: PropTypes.bool,
  plainLayout: PropTypes.bool,
  dropShadowLayout: PropTypes.bool,
  showDropShadow: PropTypes.bool,
  fallbackNativeStyle: PropTypes.string,
  showOnlySiteServed: PropTypes.bool,
  isFeaturesSpecsLayout: PropTypes.bool,
  ads11455Ads11617Recipe: PropTypes.string,
  ads12450Recipe: PropTypes.string,
  onIncentivesAdLoadedCallback: PropTypes.func,
};

const IncentivesAdWrapper = withSiteServedAdWrapper(IncentivesAdUI);

export function IncentivesAd(props) {
  return <IncentivesAdWrapper {...props} adType={INCENTIVES_AD} />;
}
